/* The Loom — password reset confirm page.
 *
 * Reached from the email link `/reset-password?token=...`. Lets an
 * admin-seeded email-login user choose a new password and routes them
 * back to the landing page (where they can sign in with the new one).
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, Loader2, ShieldCheck, ArrowRight } from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import PasswordStrengthMeter, { scorePassword } from '@/components/PasswordStrengthMeter';
import { HierarchyBackButton } from '@/components/navigation/HierarchyBackButton';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function ResetPasswordPage() {
  useSeoMeta('/reset-password', {
    title: 'Reset password — The Loom',
    description: 'Choose a new password for your The Loom account.',
  });
  const navigate = useNavigate();

  const token = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return (sp.get('token') || '').trim();
  }, []);

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token.');
      return;
    }
    if (pw.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (scorePassword(pw).score < 2) {
      toast.error('Please choose a stronger password.');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await http.post('/auth/password-reset/confirm', { token, new_password: pw });
      setDone(true);
      toast.success('Password updated. Please sign in.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="reset-password-page">
      <header className="border-b border-border/60 bg-card/40 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <BookOpen className="h-3.5 w-3.5" />
            </div>
            <span className="text-[13px] font-semibold tracking-tight">Crown & Rose</span>
          </Link>
        </div>
      </header>

      <section className="mx-auto flex max-w-md flex-col px-6 py-16">
        <div className="mb-4">
          <HierarchyBackButton to="/login" label="Back to sign in" testId="reset-password-hierarchy-back" />
        </div>
        <div className="mb-5 inline-flex items-center gap-2 self-start rounded-full border border-border/70 bg-card px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          <ShieldCheck className="h-3 w-3 text-primary" /> The Loom · Password reset
        </div>

        {done ? (
          <div className="rounded-xl border border-border bg-card p-6 shadow-panel-active" data-testid="reset-success">
            <h1 className="text-xl font-semibold tracking-tight">Password updated</h1>
            <p className="mt-2 text-[13.5px] text-muted-foreground">
              Your password has been changed. Sign in with your new password to continue.
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              data-testid="reset-go-signin"
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Back to sign in <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="rounded-xl border border-border bg-card p-6 shadow-panel-active"
            data-testid="reset-form"
          >
            <h1 className="text-xl font-semibold tracking-tight">Choose a new password</h1>
            <p className="mt-1 text-[12.5px] text-muted-foreground">
              {token
                ? 'Pick a strong password (at least 8 characters).'
                : 'Your link is missing a token. Please use the link from your email.'}
            </p>

            <label className="mt-4 block">
              <span className="mb-1 block text-[11.5px] text-muted-foreground">New password</span>
              <input
                type="password"
                required
                minLength={8}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="reset-pw"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-primary"
              />
              <PasswordStrengthMeter password={pw} />
            </label>
            <label className="mt-3 block">
              <span className="mb-1 block text-[11.5px] text-muted-foreground">Confirm new password</span>
              <input
                type="password"
                required
                minLength={8}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                data-testid="reset-pw2"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-primary"
              />
            </label>

            <button
              type="submit"
              disabled={busy || !token}
              data-testid="reset-submit"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Update password
            </button>

            <p className="mt-3 text-center text-[11px] text-muted-foreground">
              <Link to="/" className="font-semibold text-primary hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
